import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { ShieldAlert } from "lucide-react";
import AuthContext from "../hooks/authContext";
import { sessionManager } from "../services/sessionManager";

/**
 * SessionWarningModal
 *
 * Shown when the user has been idle for 28 minutes (2 minutes before the 30-minute
 * idle timeout). Gives the user a chance to stay signed in or log out voluntarily.
 *
 * Countdown source:
 *   The displayed countdown is derived from sessionManager.getIdleExpiry() on every
 *   tick rather than decrementing a local counter. This ensures the displayed value
 *   is accurate even if the tab was backgrounded (where setInterval is throttled).
 *
 * Cross-tab behaviour:
 *   - "Stay Signed In" calls sessionManager.resetTimer(), which resets the idle
 *     timestamp in localStorage AND broadcasts a "session:reset" message via
 *     BroadcastChannel so all other open tabs dismiss their warning modals too.
 *   - "Log Out" calls auth.logoutuser(), which calls sessionManager.end(), which
 *     broadcasts "session:logout" so all other tabs also log out.
 *   - If the user takes no action, the sessionManager _tick() fires a timeout when
 *     the expiry passes; the timeout callback in App.js calls logoutuser(), which
 *     again broadcasts to all tabs.
 *
 * Dismissal:
 *   The modal is controlled by auth.sessionWarning (set/cleared by App.js callbacks
 *   wired to sessionManager.onWarning / sessionManager.onWarningDismiss).
 *   User activity while the modal is open also triggers _handleActivity() in the
 *   session manager, which calls _clearWarning(true) → onWarningDismiss → closes
 *   the modal and notifies other tabs.
 */
const SessionWarningModal = ({ open }) => {
  const auth = useContext(AuthContext);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const timeoutHandledRef = useRef(false);

  // Drive the countdown from the stored expiry timestamp so background-tab
  // timer throttling cannot cause the displayed value to drift.
  useEffect(() => {
    if (!open) return;

    const tick = () => {
      const expiry = sessionManager.getIdleExpiry();
      if (expiry === null) {
        setSecondsLeft(0);
        return;
      }
      const remaining = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };

    tick(); // compute immediately so there is no 1-second delay on open
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (!open) {
      timeoutHandledRef.current = false;
      return;
    }
    if (secondsLeft > 0 || timeoutHandledRef.current) return;
    timeoutHandledRef.current = true;
    auth.logoutuser();
  }, [open, secondsLeft, auth]);

  const handleStaySignedIn = () => {
    // resetTimer() resets the idle expiry in localStorage, clears _warnFired,
    // calls onWarningDismiss (→ setSessionWarning(false) in App.js), and
    // broadcasts "session:reset" to all other authenticated tabs.
    sessionManager.resetTimer();
  };

  const handleLogout = () => {
    // Uses the canonical logout path — same as manual logout and 401-triggered logout.
    auth.logoutuser();
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const countdownDisplay = `${minutes}:${String(seconds).padStart(2, "0")}`;

  // Urgency colour: amber at 2 min, transitions to red below 30 s.
  const countdownColor = secondsLeft <= 30 ? "#ef4444" : "#f59e0b";

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          zIndex: 9999,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1.5,
          }}
        >
          <ShieldAlert size={44} color="#f59e0b" strokeWidth={1.75} />

          <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
            Your session is about to expire
          </Typography>

          <Typography variant="body2" color="text.secondary">
            You will be automatically signed out due to inactivity in
          </Typography>

          {/* Live countdown */}
          <Box
            sx={{
              mt: 0.5,
              px: 3,
              py: 1.5,
              borderRadius: 1.5,
              backgroundColor: secondsLeft <= 30 ? "#fef2f2" : "#fffbeb",
              border: `1px solid ${secondsLeft <= 30 ? "#fecaca" : "#fde68a"}`,
              minWidth: 120,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontFamily: "monospace",
                color: countdownColor,
                letterSpacing: "0.05em",
                transition: "color 0.3s",
              }}
            >
              {countdownDisplay}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Any unsaved changes may be lost if you are signed out.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleLogout}
          variant="outlined"
          fullWidth
          sx={{
            color: "text.primary",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          Sign Out
        </Button>

        <Button
          onClick={handleStaySignedIn}
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: "#078037",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#05662a",
            },
          }}
        >
          Stay Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarningModal;
